/* eslint-disable jsdoc/require-jsdoc, no-nested-ternary, consistent-return */
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axiosInstance from '../_helpers/interceptor';
import { DEV_DOMAIN_URL } from '../environment';
import { getUserNameFromSessionStorage } from '../services/session-storage-service';
import { DeviceSubscription, getAdminClients } from '../services/dashboard-service';

const ADMIN_EMAIL = 'suresh@potsdamsensors.com';

type Subscription = {
  id: number;
  clientId: number;
  /** Device the plan covers; null on legacy account-level rows. */
  serialNumber: number | null;
  planName: string;
  startDate: string | null;
  endDate: string | null;
  status: string;
  paypalOrderId: string;
};

type Payment = {
  id: number;
  subscriptionId: number;
  paypalPaymentId: string;
  paypalPayerId: string;
  amount: number;
  currency: string;
  status: string;
  paymentDate: string | null;
};

type Client = { id: number; clientName: string };

const GRANTABLE_PLANS = ['Enterprise', 'Professional', 'Advanced', 'Basic', 'Free'];

function formatDate(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function statusBadge(status: string): string {
  const s = (status || '').toUpperCase();
  if (s === 'SUCCESS' || s === 'APPROVED')
    return 'bg-success-500/15 text-success-400 border-success-500/30';
  if (s === 'PENDING') return 'bg-warning-500/15 text-warning-300 border-warning-500/30';
  if (s === 'FAILED') return 'bg-error-500/15 text-error-400 border-error-500/30';
  if (s === 'EXPIRED') return 'bg-gray-500/15 text-gray-400 border-gray-500/30';
  return 'bg-gray-500/15 text-gray-400 border-gray-500/30';
}

/**
 * Puts devices of a client on a plan without a PayPal checkout (e.g. A quoted Enterprise deal).
 * @param root0 - Component props.
 * @param root0.clients - Clients to choose from.
 * @param root0.onGranted - Called after a successful grant.
 * @returns The grant form.
 */
function GrantDevicePlan({
  clients,
  onGranted
}: {
  clients: Client[];
  onGranted: () => void;
}): ReactElement {
  const [clientId, setClientId] = useState<string>('');
  const [devices, setDevices] = useState<DeviceSubscription[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [planName, setPlanName] = useState('Enterprise');
  const [endDate, setEndDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    setDevices([]);
    setSelected(new Set());
    setMessage(null);
    if (!clientId) return;
    let cancelled = false;
    axiosInstance
      .get(`${DEV_DOMAIN_URL}/paypal/admin/clients/${clientId}/devices`)
      .then((res) => {
        if (!cancelled) setDevices(res.data || []);
      })
      .catch((e) => {
        if (!cancelled) setMessage({ ok: false, text: e?.message || 'Failed to load devices' });
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const toggle = (serial: number): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(serial)) next.delete(serial);
      else next.add(serial);
      return next;
    });
  };

  const grant = async (): Promise<void> => {
    setBusy(true);
    setMessage(null);
    try {
      await axiosInstance.post(`${DEV_DOMAIN_URL}/paypal/admin/subscriptions/grant`, {
        clientId: Number(clientId),
        serialNumbers: Array.from(selected),
        planName,
        // End of the chosen day; empty means the plan does not lapse.
        endDate: endDate ? new Date(`${endDate}T23:59:59`).toISOString() : null
      });
      setMessage({ ok: true, text: `Granted ${planName} to ${selected.size} device(s).` });
      setSelected(new Set());
      const res = await axiosInstance.get(`${DEV_DOMAIN_URL}/paypal/admin/clients/${clientId}/devices`);
      setDevices(res.data || []);
      onGranted();
    } catch (e: any) {
      setMessage({ ok: false, text: e?.response?.data || e?.message || 'Grant failed' });
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    'rounded border border-gray-700 bg-background-500 px-3 py-1.5 text-sm text-gray-200';

  return (
    <section className="rounded-lg border border-gray-800 bg-container-500">
      <div className="border-b border-gray-800 px-5 py-3">
        <h2 className="text-lg font-semibold">Grant device plan</h2>
        <p className="text-xs text-gray-400">
          Puts devices on a plan without PayPal, e.g. a quoted Enterprise deal. The grant becomes
          each device&apos;s active plan immediately.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-4 px-5 py-4">
        <label htmlFor="grant-client" className="flex flex-col gap-1 text-xs text-gray-400">
          Client
          <select id="grant-client" className={inputClass} value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Select a client…</option>
            {[...clients]
              .sort((a, b) => (a.clientName || '').localeCompare(b.clientName || ''))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.clientName} (#{c.id})
                </option>
              ))}
          </select>
        </label>
        <label htmlFor="grant-plan" className="flex flex-col gap-1 text-xs text-gray-400">
          Plan
          <select id="grant-plan" className={inputClass} value={planName} onChange={(e) => setPlanName(e.target.value)}>
            {GRANTABLE_PLANS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor="grant-end-date" className="flex flex-col gap-1 text-xs text-gray-400">
          Renewal date (blank = no expiry)
          <input
            id="grant-end-date"
            type="date"
            className={inputClass}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={busy || !clientId || selected.size === 0}
          onClick={grant}
          className="rounded border border-primary-500/40 bg-primary-500/10 px-4 py-1.5 text-sm text-primary-300 hover:bg-primary-500/20 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy ? 'Granting…' : `Grant to ${selected.size} device${selected.size !== 1 ? 's' : ''}`}
        </button>
      </div>
      {message && (
        <div className={`px-5 pb-3 text-sm ${message.ok ? 'text-success-400' : 'text-error-400'}`}>
          {String(message.text)}
        </div>
      )}
      {clientId && (
        <div className="overflow-x-auto border-t border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-background-500 text-left text-gray-400">
              <tr>
                <th className="px-4 py-2">
                  <input
                    type="checkbox"
                    className="accent-primary-500"
                    aria-label="Select all devices"
                    checked={devices.length > 0 && selected.size === devices.length}
                    onChange={() =>
                      setSelected(
                        selected.size === devices.length
                          ? new Set()
                          : new Set(devices.map((d) => d.serialNumber))
                      )
                    }
                  />
                </th>
                <th className="px-4 py-2">Device</th>
                <th className="px-4 py-2">Current plan</th>
                <th className="px-4 py-2">Renewal date</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.serialNumber} className="border-t border-gray-800">
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      className="accent-primary-500"
                      aria-label={`Select device ${d.serialNumber}`}
                      checked={selected.has(d.serialNumber)}
                      onChange={() => toggle(d.serialNumber)}
                    />
                  </td>
                  <td className="px-4 py-2">
                    {d.deviceName} <span className="text-xs text-gray-500">#{d.serialNumber}</span>
                  </td>
                  <td className="px-4 py-2">{d.planName || <span className="text-gray-500">No plan</span>}</td>
                  <td className="px-4 py-2">
                    {d.planName ? (d.endDate ? formatDate(d.endDate) : 'No expiry') : '—'}
                  </td>
                </tr>
              ))}
              {devices.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-center text-gray-500" colSpan={4}>
                    No devices
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function AdminPaymentsPage(): ReactElement {
  const userEmail = useMemo(() => getUserNameFromSessionStorage(), []);
  const isAllowed = userEmail.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!isAllowed) return;
    getAdminClients()
      .then((res) => setClients(res.data || []))
      .catch(() => setClients([]));
  }, [isAllowed]);

  const clientNames = useMemo(() => {
    const names: Record<number, string> = {};
    clients.forEach((c) => {
      names[c.id] = c.clientName;
    });
    return names;
  }, [clients]);

  useEffect(() => {
    if (!isAllowed) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const [subsRes, paysRes] = await Promise.all([
          axiosInstance.get(`${DEV_DOMAIN_URL}/paypal/admin/subscriptions`),
          axiosInstance.get(`${DEV_DOMAIN_URL}/paypal/admin/payments`)
        ]);
        if (cancelled) return;
        setSubscriptions(subsRes.data || []);
        setPayments(paysRes.data || []);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || 'Failed to load admin data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAllowed, reloadKey]);

  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }

  const totalCollected = payments
    .filter((p) => (p.status || '').toLowerCase() === 'approved')
    .reduce((acc, p) => acc + (p.amount || 0), 0);

  return (
    <div className="flex flex-1 flex-col gap-6 px-6 py-6 text-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Payments & Subscriptions (Admin)</h1>
          <p className="text-sm text-gray-400">Signed in as {userEmail}</p>
        </div>
        <div className="flex gap-3">
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Subscriptions</div>
            <div className="text-xl font-semibold">{subscriptions.length}</div>
          </div>
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Payments</div>
            <div className="text-xl font-semibold">{payments.length}</div>
          </div>
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Total Collected</div>
            <div className="text-xl font-semibold">${totalCollected.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-gray-400">
          <span className="material-icons-outlined animate-spin">autorenew</span>
          Loading…
        </div>
      ) : error ? (
        <div className="rounded border border-error-500/40 bg-error-500/10 px-4 py-3 text-error-400">
          {error}
        </div>
      ) : (
        <>
          <GrantDevicePlan clients={clients} onGranted={() => setReloadKey((k) => k + 1)} />

          <section className="rounded-lg border border-gray-800 bg-container-500">
            <div className="flex items-center justify-between border-b border-gray-800 px-5 py-3">
              <h2 className="text-lg font-semibold">Device subscriptions</h2>
              <span className="text-sm text-gray-400">{subscriptions.length} records</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background-500 text-left text-gray-400">
                  <tr>
                    <th className="px-4 py-2">ID</th>
                    <th className="px-4 py-2">Client</th>
                    <th className="px-4 py-2">Device</th>
                    <th className="px-4 py-2">Plan</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Start</th>
                    <th className="px-4 py-2">Renewal</th>
                    <th className="px-4 py-2">PayPal Order</th>
                  </tr>
                </thead>
                <tbody>
                  {[...subscriptions]
                    .sort((a, b) => b.id - a.id)
                    .map((s) => (
                      <tr key={s.id} className="border-t border-gray-800">
                        <td className="px-4 py-2 font-mono">{s.id}</td>
                        <td className="px-4 py-2">
                          {clientNames[s.clientId] ? `${clientNames[s.clientId]} (#${s.clientId})` : s.clientId}
                        </td>
                        <td className="px-4 py-2">
                          {s.serialNumber !== null && s.serialNumber !== undefined ? (
                            `#${s.serialNumber}`
                          ) : (
                            <span className="text-gray-500" title="Account-level plan from before plans moved to devices">
                              Legacy (account)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2">{s.planName}</td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-block rounded border px-2 py-0.5 text-xs ${statusBadge(
                              s.status
                            )}`}
                          >
                            {s.status}
                          </span>
                        </td>
                        <td className="px-4 py-2">{formatDate(s.startDate)}</td>
                        <td className="px-4 py-2">{formatDate(s.endDate)}</td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-400">
                          {s.paypalOrderId || '—'}
                        </td>
                      </tr>
                    ))}
                  {subscriptions.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-center text-gray-500" colSpan={8}>
                        No subscriptions
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-lg border border-gray-800 bg-container-500">
            <div className="flex items-center justify-between border-b border-gray-800 px-5 py-3">
              <h2 className="text-lg font-semibold">Payments</h2>
              <span className="text-sm text-gray-400">{payments.length} records</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background-500 text-left text-gray-400">
                  <tr>
                    <th className="px-4 py-2">ID</th>
                    <th className="px-4 py-2">Subscription</th>
                    <th className="px-4 py-2">Amount</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Date</th>
                    <th className="px-4 py-2">PayPal Payment</th>
                    <th className="px-4 py-2">Payer</th>
                  </tr>
                </thead>
                <tbody>
                  {[...payments]
                    .sort((a, b) => b.id - a.id)
                    .map((p) => (
                      <tr key={p.id} className="border-t border-gray-800">
                        <td className="px-4 py-2 font-mono">{p.id}</td>
                        <td className="px-4 py-2">{p.subscriptionId}</td>
                        <td className="px-4 py-2">
                          {p.currency} {p.amount?.toFixed(2)}
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-block rounded border px-2 py-0.5 text-xs ${statusBadge(
                              p.status
                            )}`}
                          >
                            {p.status}
                          </span>
                        </td>
                        <td className="px-4 py-2">{formatDate(p.paymentDate)}</td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-400">
                          {p.paypalPaymentId}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-400">
                          {p.paypalPayerId}
                        </td>
                      </tr>
                    ))}
                  {payments.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-center text-gray-500" colSpan={7}>
                        No payments
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
